/**
 * The API client — DESIGN.md §12.1, §9.1.
 *
 * One `fetch` wrapper for the whole app. It owns three things nothing else
 * should have to remember:
 *  - the session cookie (`credentials: 'include'`),
 *  - the CSRF header every state-changing request must carry (§9.1),
 *  - turning the documented `{error:{code,message}}` body into a typed
 *    `ApiError` the UI can branch on.
 */
import {
  CLIENT_HEADER,
  CLIENT_HEADER_VALUE,
  type ApiErrorBody,
  type ErrorCode,
} from '@slate/shared';

export const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the right response is "sign in again", not "show an error". */
  get isAuthError(): boolean {
    return this.code === 'UNAUTHENTICATED' || this.code === 'ACCOUNT_INACTIVE';
  }

  /** §6.2 — the card was edited underneath us; refresh rather than clobber. */
  get isStale(): boolean {
    return this.code === 'STALE_UPDATE' || this.code === 'CONFLICT';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Query parameters; `undefined` and `null` entries are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, query } = options;

  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = {
    accept: 'application/json',
    // Sent on every request, not just unsafe ones: it costs nothing and means
    // a future endpoint cannot forget it (§9.1).
    [CLIENT_HEADER]: CLIENT_HEADER_VALUE,
  };
  if (body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      credentials: 'include',
      signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError('NETWORK_ERROR', 'Could not reach Slate — check your connection', 0);
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const parsed = payload as Partial<ApiErrorBody>;
    throw new ApiError(
      parsed.error?.code ?? 'INTERNAL_ERROR',
      parsed.error?.message ?? `Request failed (${response.status})`,
      response.status,
      parsed.error?.details,
    );
  }

  return payload as T;
}

export const http = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    api<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    api<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    api<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    api<T>(path, { ...options, method: 'PATCH', body }),
  /**
   * DELETE takes an optional body: a dependency edge is identified by both of
   * its ends (§6.5), and a project delete carries the typed key confirmation
   * (§6.1). Both are DELETEs with something to say.
   */
  delete: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    api<T>(path, { ...options, method: 'DELETE', body }),
};
